import { useContext, useCallback, FC, useMemo, useState, useEffect, createContext, memo } from 'react';
import { AnimatePresence } from 'motion/react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCrown } from '@fortawesome/pro-solid-svg-icons';

import { useUser } from '@context/user-context';
import useNode from '@hooks/use-node';
import useIntersection from '@hooks/use-intersection';
import { useTeamRole } from '@hooks/team-workspace';
import { useActorPlan } from '@hooks/actor/use-actor-plan';

import { analytics } from '@modules/analytics';
import { logger } from '@modules/logger';

import { layoutContext } from '@context/layout';

import Button from '@components/Button/Button';

import { useLegacyIsActive } from './hooks';

import { NavHeader } from './NavHeader/NavHeader';
import { NavFooter } from './NavFooter/NavFooter';
// import { NavListAlt } from './NavList';
import { TeamMemberHighlightPopup } from './HighlightPopup/team-member';
import { TeamSignatureHighlightPopup } from './HighlightPopup/email-signature';
import { CardHighlightPopup, TeamCardHighlightPopup } from './HighlightPopup';

import styles from './SideNav.module.scss';
import { ModalType, useGlobalModal } from '@modules/global-modals';

import dynamic from 'next/dynamic';

const NavListNoSSR = dynamic(() => import('@components/SideNav/NavList'), { ssr: false });

interface SideNavContextValues {
  onSelectItem: React.MouseEventHandler;
}

const SideNavContext = createContext<SideNavContextValues>({
  onSelectItem: () => logger.warn('onSelectItem was not provided for sideNavContext'),
});

/**
 * Detects if the content of the sideNav (i.e. the navigation links) overflows it's container.
 */
const useContentOverflowDetection = (): [
  boolean,
  (node: HTMLElement | null) => void,
  (node: HTMLElement | null) => void,
] => {
  const [showSeparator, setShowSeparator] = useState(false);

  const onIntersect = useCallback((entries: IntersectionObserverEntry[]) => {
    const navContents = entries[0];
    setShowSeparator(!navContents.isIntersecting);
  }, []);

  // We want to observe when the chips are inside their container which has a bounded height
  // Threshold set to 0.98 instead of 1 to make it less sensitive while animating. I think the
  // items take up more space than in the navbar for a fraction of a second so having it at 1
  // causes the border to flicker
  const [rootRef, navContentsRef] = useIntersection(onIntersect, { threshold: 0.98 });

  return [showSeparator, rootRef, navContentsRef];
};

const useSideNav = () => {
  const [showSeparator, rootRef, navContentsRef] = useContentOverflowDetection();

  return {
    rootRef,
    navContentsRef,
    showSeparator,
  };
};

const QuickLinksSection: FC<
  React.PropsWithChildren<{ setScrollableContainerNode: (instance: HTMLDivElement | null) => void }>
> = ({ children, setScrollableContainerNode }) => {
  return (
    <div ref={setScrollableContainerNode} className={styles['quick-links-section']}>
      <ul className={styles['quick-links']}>
        <li className={styles['quick-links-list']}>{children}</li>
      </ul>
    </div>
  );
};

const NewSideNav: React.FC = () => {
  const { state: navState } = useContext(layoutContext);
  const { rootRef } = useSideNav();

  const userDetails = useUser();
  const { user, userTags, loading: userLoading } = userDetails;
  const { atLeastRole, loading: teamRoleLoading } = useTeamRole();
  const { planType, loading: billingSubscriptionLoading } = useActorPlan();
  const { openModal } = useGlobalModal({ trackingSource: 'side-nav', registeredModals: [ModalType.UPGRADE] });

  const dataLoading = userLoading || teamRoleLoading;

  const isTeamAdmin = atLeastRole('ADMIN');

  const [showBorder, setShowBorder] = useState(false);
  const [scrollableContainerNode, setScrollableContainerNode] = useNode<HTMLDivElement>();

  const showTutorialAndOnboardingButtons = useMemo(() => {
    return Boolean(user) && isTeamAdmin && userTags?.canBookOnboarding;
  }, [isTeamAdmin, user, userTags?.canBookOnboarding]);

  const showUpgradePromptFooter = useMemo(() => {
    return !!user && planType === 'free' && !billingSubscriptionLoading;
  }, [planType, user, billingSubscriptionLoading]);

  function onIntersect(entries: IntersectionObserverEntry[]) {
    setShowBorder(!entries[0].isIntersecting);
  }

  const { handleSelectItem } = useLegacyIsActive();

  useEffect(() => {
    // We want to observe when the chips are inside their container which has a bounded height
    // Threshold set to 0.98 instead of 1 to make it less sensitive while animating. I think the
    // items take up more space than in the navbar for a fraction of a second so having it at 1
    // causes the border to flicker
    function initObserver() {
      const rootEl = scrollableContainerNode;
      if (!rootEl) return;
      const itemsToObserve = rootEl.querySelector(`.${styles['quick-links']}`);
      if (!itemsToObserve) return;

      const observer = new IntersectionObserver(onIntersect, { threshold: 0.98, root: rootEl });

      observer.observe(itemsToObserve);

      return () => observer.unobserve(itemsToObserve);
    }

    if (window.IntersectionObserver) {
      return initObserver();
    }
  }, [scrollableContainerNode]);

  return (
    <SideNavContext.Provider value={{ onSelectItem: handleSelectItem }}>
      <nav
        className={styles['side-nav']}
        data-collapsed={navState.collapsed}
        data-minimized={navState.minimized}
        data-has-changed={navState.overridden}
        ref={rootRef}
      >
        <NavHeader openUpgradeDialog={() => openModal(ModalType.UPGRADE)} />

        <QuickLinksSection setScrollableContainerNode={setScrollableContainerNode}>
          <NavListNoSSR loading={dataLoading} isTeamAdmin={isTeamAdmin} userDetails={userDetails} />
        </QuickLinksSection>

        {!dataLoading && showUpgradePromptFooter && (
          <NavFooter showBorder={showBorder}>
            <div className={styles['nav-footer-btn-container']}>
              <Button
                variant="subtle"
                type="default"
                onClick={() => {
                  analytics.track({ name: 'navbarupgradeaccountbutton_clicked' });
                  openModal(ModalType.UPGRADE);
                }}
                className={styles['nav-footer-btn']}
              >
                <FontAwesomeIcon
                  data-collapsed={navState.collapsed}
                  icon={faCrown}
                  className={styles['nav-footer-btn-icon']}
                />
                {!navState.collapsed && (
                  <span className={styles['nav-footer-btn-text']}>Upgrade account</span>
                )}
              </Button>
            </div>
          </NavFooter>
        )}

        {!dataLoading && showTutorialAndOnboardingButtons && (
          <AnimatePresence initial={false}>
            <NavFooter display={!navState.collapsed} showBorder={showBorder}>
              <div className={styles['help-btns']} id="help-btns">
                <div className={styles['nav-footer-btn-container']}>
                  <a
                    href="https://www.youtube.com/watch?v=alVCruuHM6U"
                    className={styles['footer-link']}
                    target="_blank"
                    rel="noreferrer"
                  >
                    View tutorial video
                  </a>
                </div>
                <div className={styles['nav-footer-btn-container']}>
                  <a
                    href="https://meetings.hubspot.com/blinq-growth/onboarding"
                    className={styles['footer-link']}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Book an onboarding session
                  </a>
                </div>
              </div>
            </NavFooter>
          </AnimatePresence>
        )}
      </nav>

      <TeamMemberHighlightPopup />
      <CardHighlightPopup />
      <TeamCardHighlightPopup />
      <TeamSignatureHighlightPopup />
    </SideNavContext.Provider>
  );
};

export { SideNavContext };
export default memo(NewSideNav);
